import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import AdminLayout from './AdminLayout';
import { getSettings, updateSettings } from '../../services/apiService';

const Settings = () => {
  const [userEmail, setUserEmail] = useState('');
  const [settings, setSettings] = useState({
    siteName: 'MedAgriCycle',
    siteDescription: 'Mediterranean agricultural innovation platform',
    email: 'contact@medagricycle.eu',
    phone: '+00 00 00 00 00',
    facebook: '',
    twitter: '',
    linkedin: '',
    instagram: '',
    youtube: ''
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [notification, setNotification] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  const navigate = useNavigate();
  const { t } = useTranslation();

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Show notifications
  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => {
      setNotification(null);
    }, 3000);
  };

  useEffect(() => {
    const isLoggedIn = localStorage.getItem('isLoggedIn');
    const email = localStorage.getItem('userEmail');
    
    if (!isLoggedIn || !email) {
      navigate('/login');
      return;
    }
    
    setUserEmail(email);
    fetchSettings();
  }, [navigate]);

  // Load settings from the API
  const fetchSettings = async () => {
    try {
      setIsLoading(true);
      const data = await getSettings();
      if (data) {
        setSettings(data);
      }
    } catch (error) {
      showNotification('Error loading settings', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSettingChange = (key, value) => {
    setSettings({ ...settings, [key]: value });
  };

  const handleSaveSettings = async () => {
    // Validation
    if (!settings.siteName || !settings.email) {
      showNotification('Site name and email are required', 'error');
      return;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(settings.email)) {
      showNotification('Invalid email format', 'error');
      return;
    }

    try {
      setIsSaving(true);
      const updatedSettings = await updateSettings(settings);
      
      if (updatedSettings) {
        setSettings(updatedSettings);
        showNotification('Settings saved successfully!', 'success');
      } else {
        showNotification('Error saving settings', 'error');
      }
    } catch (error) {
      showNotification('Error saving settings: ' + (error.message || 'Unknown error'), 'error');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <AdminLayout>
      {/* Header */}
      <div style={{
        background: 'linear-gradient(135deg, #96ca2d 0%, #7fb800 100%)',
        borderRadius: isMobile ? '12px' : '16px',
        padding: isMobile ? '16px' : '24px',
        marginBottom: isMobile ? '16px' : '24px',
        color: 'white',
        position: 'relative',
        overflow: 'hidden',
        boxShadow: '0 8px 25px rgba(150, 202, 45, 0.2)'
      }}>
        <div style={{
          position: 'absolute',
          top: '-20%',
          right: '-10%',
          width: '200px',
          height: '200px',
          background: 'rgba(255, 255, 255, 0.1)',
          borderRadius: '50%',
          zIndex: 1
        }} />
        
        <div style={{ position: 'relative', zIndex: 2 }}>
          <h1 style={{
            fontSize: 'clamp(24px, 3vw, 36px)',
            fontWeight: '800',
            margin: '0 0 8px 0',
            display: 'flex',
            alignItems: 'center',
            gap: '16px'
          }}>
            <i className="fas fa-cog" />
            Site Settings
          </h1>
          <p style={{
            fontSize: '16px',
            margin: '0 0 24px 0',
            opacity: 0.9
          }}>
            Configure your site's general settings
          </p>
        </div>
      </div>

      {/* Notification Toast */}
      {notification && (
        <div style={{
          position: 'fixed',
          top: isMobile ? '10px' : '20px',
          right: isMobile ? '10px' : '20px',
          left: isMobile ? '10px' : 'auto',
          padding: isMobile ? '12px 16px' : '16px 20px',
          background: notification.type === 'success' ? '#10b981' : '#ef4444',
          color: 'white',
          borderRadius: '12px',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
          zIndex: 10000,
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          animation: 'slideIn 0.3s ease-out',
          maxWidth: isMobile ? 'calc(100% - 20px)' : 'auto',
          width: isMobile ? 'auto' : 'auto'
        }}>
          <i className={`fas ${notification.type === 'success' ? 'fa-check-circle' : 'fa-exclamation-circle'}`} />
          <span>{notification.message}</span>
        </div>
      )}

      {/* Settings Form */}
      {isLoading ? (
        <div style={{
          background: 'white',
          borderRadius: '24px',
          padding: '64px',
          boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)',
          textAlign: 'center'
        }}>
          <i className="fas fa-spinner fa-spin" style={{ fontSize: '32px', color: '#96ca2d', marginBottom: '16px' }} />
          <p style={{ color: '#6b7280', fontSize: '14px' }}>Loading settings...</p>
        </div>
      ) : (
        <div style={{
          background: 'white',
          borderRadius: isMobile ? '16px' : '24px',
          padding: isMobile ? '20px' : '32px',
          boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)',
          border: '1px solid rgba(0, 0, 0, 0.05)'
        }}>
        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(300px, 1fr))',
          gap: isMobile ? '20px' : '24px',
          marginBottom: isMobile ? '24px' : '32px'
        }}>
          {/* Site Information */}
          <div>
            <h3 style={{
              fontSize: '18px',
              fontWeight: '700',
              margin: '0 0 16px 0',
              color: '#1f2937',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <i className="fas fa-info-circle" style={{ color: '#3b82f6' }} />
              Site Information
            </h3>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#374151',
                  marginBottom: '8px'
                }}>
                  Site name
                </label>
                <input
                  type="text"
                  value={settings.siteName}
                  onChange={(e) => handleSettingChange('siteName', e.target.value)}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    fontSize: '14px',
                    transition: 'border-color 0.3s ease'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#3b82f6';
                    e.target.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#d1d5db';
                    e.target.style.boxShadow = 'none';
                  }}
                />
              </div>

              <div>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#374151',
                  marginBottom: '8px'
                }}>
                  Description
                </label>
                <textarea
                  value={settings.siteDescription}
                  onChange={(e) => handleSettingChange('siteDescription', e.target.value)}
                  rows={3}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    fontSize: '14px',
                    resize: 'vertical',
                    transition: 'border-color 0.3s ease'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#3b82f6';
                    e.target.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#d1d5db';
                    e.target.style.boxShadow = 'none';
                  }}
                />
              </div>
            </div>
          </div>

          {/* Contact Information */}
          <div>
            <h3 style={{
              fontSize: '18px',
              fontWeight: '700',
              margin: '0 0 16px 0',
              color: '#1f2937',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <i className="fas fa-envelope" style={{ color: '#10b981' }} />
              Contact Information
            </h3>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#374151',
                  marginBottom: '8px'
                }}>
                  Contact email
                </label>
                <input
                  type="email"
                  value={settings.email}
                  onChange={(e) => handleSettingChange('email', e.target.value)}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    fontSize: '14px',
                    transition: 'border-color 0.3s ease'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#10b981';
                    e.target.style.boxShadow = '0 0 0 3px rgba(16, 185, 129, 0.1)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#d1d5db';
                    e.target.style.boxShadow = 'none';
                  }}
                />
              </div>

              <div>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#374151',
                  marginBottom: '8px'
                }}>
                  Phone
                </label>
                <input
                  type="tel"
                  value={settings.phone}
                  onChange={(e) => handleSettingChange('phone', e.target.value)}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    fontSize: '14px',
                    transition: 'border-color 0.3s ease'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#10b981';
                    e.target.style.boxShadow = '0 0 0 3px rgba(16, 185, 129, 0.1)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#d1d5db';
                    e.target.style.boxShadow = 'none';
                  }}
                />
              </div>
            </div>
          </div>

          {/* Social Media */}
          <div>
            <h3 style={{
              fontSize: '18px',
              fontWeight: '700',
              margin: '0 0 16px 0',
              color: '#1f2937',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <i className="fas fa-share-alt" style={{ color: '#8b5cf6' }} />
              Social Media
            </h3>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#374151',
                  marginBottom: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <i className="fab fa-facebook-f" style={{ color: '#1877f2' }} />
                  Facebook
                </label>
                <input
                  type="url"
                  value={settings.facebook}
                  onChange={(e) => handleSettingChange('facebook', e.target.value)}
                  placeholder="https://www.facebook.com/your-page"
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    fontSize: '14px',
                    transition: 'border-color 0.3s ease'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#1877f2';
                    e.target.style.boxShadow = '0 0 0 3px rgba(24, 119, 242, 0.1)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#d1d5db';
                    e.target.style.boxShadow = 'none';
                  }}
                />
              </div>

              <div>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#374151',
                  marginBottom: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <i className="fab fa-twitter" style={{ color: '#1da1f2' }} />
                  Twitter
                </label>
                <input
                  type="url"
                  value={settings.twitter}
                  onChange={(e) => handleSettingChange('twitter', e.target.value)}
                  placeholder="https://www.twitter.com/your-account"
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    fontSize: '14px',
                    transition: 'border-color 0.3s ease'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#1da1f2';
                    e.target.style.boxShadow = '0 0 0 3px rgba(29, 161, 242, 0.1)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#d1d5db';
                    e.target.style.boxShadow = 'none';
                  }}
                />
              </div>

              <div>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#374151',
                  marginBottom: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <i className="fab fa-linkedin-in" style={{ color: '#0077b5' }} />
                  LinkedIn
                </label>
                <input
                  type="url"
                  value={settings.linkedin}
                  onChange={(e) => handleSettingChange('linkedin', e.target.value)}
                  placeholder="https://www.linkedin.com/company/your-company"
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    fontSize: '14px',
                    transition: 'border-color 0.3s ease'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#0077b5';
                    e.target.style.boxShadow = '0 0 0 3px rgba(0, 119, 181, 0.1)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#d1d5db';
                    e.target.style.boxShadow = 'none';
                  }}
                />
              </div>

              <div>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#374151',
                  marginBottom: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <i className="fab fa-instagram" style={{ color: '#e4405f' }} />
                  Instagram
                </label>
                <input
                  type="url"
                  value={settings.instagram}
                  onChange={(e) => handleSettingChange('instagram', e.target.value)}
                  placeholder="https://www.instagram.com/your-account"
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    fontSize: '14px',
                    transition: 'border-color 0.3s ease'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#e4405f';
                    e.target.style.boxShadow = '0 0 0 3px rgba(228, 64, 95, 0.1)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#d1d5db';
                    e.target.style.boxShadow = 'none';
                  }}
                />
              </div>

              <div>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#374151',
                  marginBottom: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <i className="fab fa-youtube" style={{ color: '#ff0000' }} />
                  YouTube
                </label>
                <input
                  type="url"
                  value={settings.youtube}
                  onChange={(e) => handleSettingChange('youtube', e.target.value)}
                  placeholder="https://www.youtube.com/channel/your-channel"
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    fontSize: '14px',
                    transition: 'border-color 0.3s ease'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#ff0000';
                    e.target.style.boxShadow = '0 0 0 3px rgba(255, 0, 0, 0.1)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#d1d5db';
                    e.target.style.boxShadow = 'none';
                  }}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Save Button */}
        <div style={{
          display: 'flex',
          justifyContent: isMobile ? 'stretch' : 'flex-end',
          paddingTop: isMobile ? '20px' : '24px',
          borderTop: '1px solid #e5e7eb'
        }}>
          <button
            onClick={handleSaveSettings}
            disabled={isSaving}
            style={{
              background: isSaving 
                ? '#9ca3af' 
                : 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
              border: 'none',
              borderRadius: isMobile ? '10px' : '12px',
              padding: isMobile ? '12px 20px' : '12px 24px',
              color: 'white',
              cursor: isSaving ? 'not-allowed' : 'pointer',
              fontSize: isMobile ? '13px' : '14px',
              fontWeight: '600',
              transition: 'all 0.3s ease',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              boxShadow: isSaving 
                ? 'none' 
                : '0 4px 15px rgba(245, 158, 11, 0.3)',
              opacity: isSaving ? 0.7 : 1,
              width: isMobile ? '100%' : 'auto'
            }}
            onMouseEnter={(e) => {
              if (!isSaving) {
                e.target.style.transform = 'translateY(-2px)';
                e.target.style.boxShadow = '0 6px 20px rgba(245, 158, 11, 0.4)';
              }
            }}
            onMouseLeave={(e) => {
              if (!isSaving) {
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = '0 4px 15px rgba(245, 158, 11, 0.3)';
              }
            }}
          >
            {isSaving ? (
              <>
                <i className="fas fa-spinner fa-spin" />
                Saving...
              </>
            ) : (
              <>
                <i className="fas fa-save" />
                Save Settings
              </>
            )}
          </button>
        </div>
        </div>
      )}
      <style>{`
        @keyframes slideIn {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
      `}</style>
    </AdminLayout>
  );
};

export default Settings;